import { LitElement, html, css } from "lit";

/**
 * Switch button, based on a native checkbox.
 * @class Panoramax.components.ui.Switch
 * @element pnx-switch
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @fires Panoramax.components.ui.Switch#change
 * @example
 * ```html
 * <pnx-switch
 *   checked="true"
 *   name="my-switch"
 *   @change=${doSomething}
 * >
 *   Label text
 * </pnx-switch>
 * ```
 */
export default class Switch extends LitElement {
	static styles = css`
		.toggle {
			cursor: pointer;
			display: inline-block;
		}

		.toggle-switch {
			display: inline-block;
			background: #ccc;
			border-radius: 11px;
			width: 42px;
			height: 22px;
			position: relative;
			vertical-align: middle;
			transition: background 0.25s;
		}
		.toggle-switch:before, .toggle-switch:after {
			content: "";
		}
		.toggle-switch:before {
			display: block;
			background: linear-gradient(to bottom, #fff 0%, #eee 100%);
			border-radius: 50%;
			box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25);
			width: 16px;
			height: 16px;
			position: absolute;
			top: 3px;
			left: 3px;
			transition: left 0.25s;
		}
		.toggle:hover .toggle-switch:before {
			background: linear-gradient(to bottom, #fff 0%, #fff 100%);
			box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.5);
		}
		.toggle-checkbox:checked + .toggle-switch {
			background: var(--widget-bg-active);
		}
		.toggle-checkbox:disabled + .toggle-switch:before,
		.toggle-checkbox:disabled:hover + .toggle-switch:before {
			background: var(--grey-pale);
			box-shadow: none;
		}
		.toggle-checkbox:disabled + .toggle-switch {
			background: var(--grey);
			box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25);
		}
		.toggle-checkbox:checked + .toggle-switch:before {
			left: 23px;
		}

		.toggle-checkbox {
			position: absolute;
			visibility: hidden;
		}

		.toggle-label {
			margin-left: 5px;
			position: relative;
			top: 2px;
		}
	`;

	/**
	 * Component properties.
	 * @memberof Panoramax.components.ui.Switch#
	 * @type {Object}
	 * @property {boolean} checked True if checked
	 * @property {boolean} disabled Inactive if true
	 * @property {boolean} value The current switch value
	 * @property {string} name The switch name (also serves as ID)
	 */
	static properties = {
		checked: {type: Boolean, reflect: true},
		disabled: {type: Boolean, reflect: true},
		name: {type: String},
	};

	constructor() {
		super();
		this.checked = false;
		this.disabled = false;
		this.name = "pnx-switch-1"; // Default name+ID to avoid issues
	}

	/** @private */
	_onChange(e) {
		this.checked = e.target.checked;
		/**
		 * Event for switch status change
		 * @event Panoramax.components.ui.Switch#change
		 * @type {Event}
		 */
		this.dispatchEvent(new Event("change"));
	}

	/** @private */
	render() {
		return html`
			<label class="toggle" for=${this.name}>
				<input
					class="toggle-checkbox"
					type="checkbox"
					id=${this.name}
					name=${this.name}
					.checked=${this.checked}
					.disabled=${this.disabled}
					@change=${this._onChange}
				/>
				<div class="toggle-switch"></div>
				<span class="toggle-label">
					<slot></slot>
				</span>
			</label>
		`;
	}
}

customElements.define("pnx-switch", Switch);
